import { Link } from "react-router-dom";
import BlogPostLayout from "./BlogPostLayout";
import { getPostBySlug } from "../../lib/blog";

const FAQ = [
  {
    q: "Peut-on élaguer quand il gèle ?",
    a: "Mieux vaut l'éviter. Par gel marqué, le bois devient cassant, les coupes cicatrisent mal et l'écorce peut se fendre. Attendez un redoux : en hiver, les journées hors gel ne manquent généralement pas.",
  },
  {
    q: "Quand tailler une haie de thuyas ou de lauriers ?",
    a: "Les haies persistantes se taillent plutôt à la fin de l'été ou au début de l'automne, puis éventuellement au printemps. Évitez les tailles sévères en plein hiver sur les conifères, qui repartent difficilement du vieux bois.",
  },
  {
    q: "Mon voisin peut-il m'obliger à couper mes branches ?",
    a: "Oui. Le Code civil permet au voisin d'exiger que les branches qui dépassent sur sa propriété soient coupées. En revanche, il ne peut pas les couper lui-même sans votre accord : c'est au propriétaire de l'arbre de le faire.",
  },
  {
    q: "L'élagage ouvre-t-il droit au crédit d'impôt ?",
    a: "La taille de haies et d'arbustes réalisée dans le cadre de l'entretien courant du jardin peut y ouvrir droit. Les travaux d'élagage et d'abattage qui demandent un savoir-faire ou du matériel spécifique en sont en principe exclus. Nous vous précisons ce qui est éligible sur le devis.",
  },
];

export default function TailleElagageAutomneHiver() {
  const post = getPostBySlug("taille-elagage-automne-hiver-bon-moment");
  return (
    <BlogPostLayout post={post} faq={FAQ}>
      <p className="article-lead">
        Quand les feuilles tombent, beaucoup se demandent s'il est temps de sortir la scie. Pour la plupart des arbres,
        oui : l'automne et l'hiver sont de bonnes périodes pour tailler et élaguer. Encore faut-il choisir la bonne
        fenêtre selon l'essence, la météo et le type de travaux. Voici nos repères pour les jardins de Creuse.
      </p>

      <h2>Pourquoi l'automne-hiver est une bonne période</h2>
      <p>
        Entre la chute des feuilles et le débourrement, l'arbre est en <strong>repos végétatif</strong>. La sève circule
        peu, les coupes « saignent » moins et la structure des branches est bien visible : on voit ce qu'on coupe et
        pourquoi. C'est aussi une période où les oiseaux ne nichent pas, ce qui évite de déranger la faune.
      </p>
      <p>
        Dans la pratique, on travaille surtout de novembre à début mars, en évitant les jours de gel marqué, de pluie
        battante et de vent fort.
      </p>

      <h2>Quel arbre, quel moment ?</h2>
      <h3>Feuillus d'ornement et grands arbres</h3>
      <p>
        Chênes, frênes, tilleuls, érables, charmes : la taille d'entretien, la suppression du bois mort et l'allègement
        de couronne se font idéalement en hiver, hors gel. Pour un élagage en hauteur ou près d'une maison, mieux vaut
        faire appel à un professionnel équipé. C'est notre métier : voir{" "}
        <Link to="/elagage-creuse">élagage et abattage en Creuse</Link>.
      </p>
      <h3>Arbres fruitiers à pépins</h3>
      <p>
        Pommiers et poiriers se taillent en fin d'hiver, de février à mars, une fois les grands froids passés. On
        distingue alors facilement les bourgeons à fruits des bourgeons à bois.
      </p>
      <h3>Arbres fruitiers à noyau</h3>
      <p>
        Cerisiers, pruniers et abricotiers supportent mal les coupes en hiver, qui favorisent les maladies du bois et
        les écoulements de gomme. On les taille plutôt après la récolte, en fin d'été.
      </p>
      <h3>Haies et conifères</h3>
      <p>
        Les haies caduques (charme, noisetier) peuvent être rabattues en hiver. Les persistants et les conifères
        préfèrent une taille de fin d'été ou de début d'automne, sans couper dans le vieux bois.
      </p>

      <h2>Taille douce ou élagage lourd ?</h2>
      <p>
        La <strong>taille douce</strong> consiste à retirer le bois mort, les branches qui se croisent et quelques
        rameaux pour aérer la couronne, sans changer la silhouette de l'arbre. Elle peut être répétée régulièrement et
        respecte la santé de l'arbre. L'<strong>élagage lourd</strong> (réduction de couronne, suppression de grosses
        branches, démontage) n'est justifié que pour des raisons de sécurité : branche menaçant une toiture, arbre
        déséquilibré, proximité d'un bâtiment. Dans ce cas, un diagnostic préalable permet de choisir les coupes
        indispensables et d'éviter d'affaiblir l'arbre inutilement.
      </p>

      <h2>Les erreurs fréquentes</h2>
      <ul>
        <li>
          <strong>L'étêtage</strong> : couper la tête d'un arbre l'affaiblit, provoque des repousses fragiles et augmente
          les risques de casse.
        </li>
        <li>
          <strong>Les grosses coupes à ras du tronc</strong> : il faut respecter le bourrelet à la base de la branche
          pour que l'arbre cicatrise.
        </li>
        <li>
          <strong>Tailler par grand froid</strong> : le bois gelé éclate et les plaies restent ouvertes plus longtemps.
        </li>
        <li>
          <strong>Intervenir près d'une ligne électrique</strong> : ne le faites jamais vous-même, contactez le
          gestionnaire du réseau.
        </li>
      </ul>

      <h2>Les règles à connaître</h2>
      <p>
        Le Code civil impose des distances de plantation par rapport à la limite de propriété : 2 mètres pour un arbre
        de plus de 2 mètres de haut, 50 centimètres pour les plantations plus basses. Pour les exploitants agricoles, la
        taille des haies est interdite du 16 mars au 15 août ; pour les particuliers, il est vivement recommandé de
        respecter cette période de nidification.
      </p>

      <h2>Profiter de l'hiver pour préparer le jardin</h2>
      <p>
        L'hiver est aussi le bon moment pour repérer les nids de chenilles processionnaires dans les pins et les anciens
        nids de frelons dans les arbres dénudés. Pour le reste du calendrier, consultez notre{" "}
        <Link to="/conseils/calendrier-tonte-taille-saisonniere">calendrier de tonte et de taille</Link>, et pour un
        suivi régulier, notre offre d'<Link to="/entretien-jardin-creuse">entretien de jardin en Creuse</Link>.
      </p>
    </BlogPostLayout>
  );
}
